import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { clearCache, setCacheUser } from "@/lib/cache";

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** Three auth states:
 *   - null  → still checking (/auth/me in flight)
 *   - true  → authenticated, `user` populated
 *   - false → not authenticated → render <Login>
 *
 *  CRITICAL: we skip the /auth/me check when the URL hash carries a
 *  `session_id=` fragment — the AuthCallback component will exchange the
 *  session_id and set the cookie first. Running /auth/me prematurely would
 *  race against the exchange and return a false 401, bouncing the user to
 *  the login screen immediately after sign-in. */
const ME_KEY = "snaplist.me";
const savedMe = () => { try { return JSON.parse(localStorage.getItem(ME_KEY) || "null"); } catch { return null; } };

export function AuthProvider({ children }) {
  // Open straight into the app with the last signed-in user; /auth/me confirms in the background.
  const [isAuthenticated, setIsAuthenticated] = useState(() => (savedMe() && !window.location.hash?.includes("session_id=") ? true : null));
  const [user, setUserState] = useState(() => { const u = savedMe(); setCacheUser(u?.user_id && `${u.user_id}:${u.email}`); return u; });
  const [authNotice, setAuthNotice] = useState("");
  const setUser = useCallback((u) => {
    setUserState(u);
    setCacheUser(u?.user_id ? `${u.user_id}:${u.email}` : "");
    try { if (u) localStorage.setItem(ME_KEY, JSON.stringify(u)); else localStorage.removeItem(ME_KEY); } catch { /* full */ }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setAuthNotice("");
      setIsAuthenticated(true);
    } catch (e) {
      // Keep a "paused" notice even if a later check only says "signed out".
      if (e?.response?.status === 403) setAuthNotice(e.response.data?.detail || "");
      if (!e?.response && savedMe()) return; // offline / network blip: stay in with the saved copy
      setUser(null);
      clearCache();
      setIsAuthenticated(false);
    }
  }, [setUser]);

  useEffect(() => {
    // Race-condition guard — see docblock above.
    if (window.location.hash?.includes("session_id=")) {
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(null);
    clearCache();
    setIsAuthenticated(false);
  }, [setUser]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, checkAuth, logout, setUser, setIsAuthenticated, authNotice }}>
      {children}
    </AuthContext.Provider>
  );
}
